import React, { useEffect, useMemo, useState, useRef } from 'react';
import { Tabs, Table, Button, Modal, Form, Input, InputNumber, Select, Space, Tag, Switch, message, Checkbox, Alert, ColorPicker } from 'antd';
import { supabase } from '../supabaseClient';
import { exportDeviceLibraryToJson, importDeviceLibraryFromJson, downloadDeviceLibraryTemplateJson } from '../utils/importExportDevices';
import { downloadTextFile, exportElementToHtml, exportElementToWord, resolveFilenamePrefix, buildFinalBaseName } from '../utils/exportUtils';
import TccOverlayModal from '../components/TccOverlayModal';
import { fetchFeederByIdAsync } from '../utils/deviceLibraryApi';
import { calculateShortCircuitIEC60909Multi } from '../utils/shortCircuit';
import { calculateProtectionSettings, validateInputs } from '../utils/protectionCalculations';
import { toNumber, normalizeCurrent, normalizeTime, normalizeCurveType } from '../utils/tccUtils';

const LatestToggle = ({ checked, onChange }) => (
  <Space style={{ marginBottom: 12 }}>
    <span>仅显示最新版本</span>
    <Switch checked={checked} onChange={onChange} />
  </Space>
);

function groupLatestByName(rows) {
  const map = new Map();
  rows.forEach(r => {
    const key = r.name || r.label || r.id;
    const current = map.get(key);
    if (!current || (r.version || 0) > (current.version || 0)) {
      map.set(key, r);
    }
  });
  return Array.from(map.values());
}

const DeviceLibraryPage = () => {
  const [activeKey, setActiveKey] = useState('feeders');
  const [onlyLatest, setOnlyLatest] = useState(true);

  // data states
  const [buses, setBuses] = useState([]);
  const [transformers, setTransformers] = useState([]);
  const [lines, setLines] = useState([]);
  const [feeders, setFeeders] = useState([]);
  const [groundTypes, setGroundTypes] = useState([]);
  const [cts, setCts] = useState([]);
  const [pts, setPts] = useState([]);
  const [relays, setRelays] = useState([]);

  const [loading, setLoading] = useState({ buses: false, transformers: false, lines: false, feeders: false, groundTypes: false, cts: false, pts: false, relays: false });

  // modal state
  const [modalOpen, setModalOpen] = useState(false);
  const [modalType, setModalType] = useState('feeders');
  const [editingRecord, setEditingRecord] = useState(null);
  const [form] = Form.useForm();

  // 导入报告
  const [importReportOpen, setImportReportOpen] = useState(false);
  const [importReport, setImportReport] = useState(null);
  // 导入选项弹窗
  const [importOptionsOpen, setImportOptionsOpen] = useState(false);
  const [importOptions, setImportOptions] = useState({ mode: 'upsert', dryRun: false, fileName: '', fileText: '' });
  // 导出选项弹窗
  const [exportOptionsOpen, setExportOptionsOpen] = useState(false);
  const [exportOptions, setExportOptions] = useState({ fileNamePrefix: 'device_library', onlyLatest: true, tables: ['ground_types','buses','transformers','lines','cts','pts','relays','feeders'] });
  // 导出报告
  const [exportReportOpen, setExportReportOpen] = useState(false);
  const [exportReport, setExportReport] = useState(null);
  const [showImportDetails, setShowImportDetails] = useState(false);
  const [showExportDetails, setShowExportDetails] = useState(false);
  const [simpleImportSummary, setSimpleImportSummary] = useState(false);
  const [simpleExportSummary, setSimpleExportSummary] = useState(false);
  const importReportRef = useRef(null);
  const exportReportRef = useRef(null);
  // 厂商曲线导入
  const [vendorImportOpen, setVendorImportOpen] = useState(false);
  const [vendorImportFileName, setVendorImportFileName] = useState('');
  const [vendorImportText, setVendorImportText] = useState('');
  const [vendorParseErrors, setVendorParseErrors] = useState([]);
  const [vendorParsedRecords, setVendorParsedRecords] = useState([]);
  // 厂商导入：统一颜色控制
  const [vendorUseUnifiedColor, setVendorUseUnifiedColor] = useState(false);
  const [vendorUnifiedColor, setVendorUnifiedColor] = useState('#13c2c2');
  // 新增：导出总条目汇总
  const exportTotalCount = useMemo(
    () => Object.values(exportReport?.counts || {}).reduce((acc, n) => acc + (n || 0), 0),
    [exportReport]
  );
  // 新增：导入汇总统计
  const importTotals = useMemo(() => {
    const valSummary = importReport?.validation?.summary || [];
    const totalValid = valSummary.reduce((acc, s) => acc + (s.valid || 0), 0);
    const totalInvalid = valSummary.reduce((acc, s) => acc + (s.invalid || 0), 0);
    const results = importReport?.dbResults || {};
    const totalInserted = Object.values(results).reduce((acc, r) => acc + (r.insertedCount || 0), 0);
    const totalUpserted = Object.values(results).reduce((acc, r) => acc + (r.upsertedCount || 0), 0);
    const totalFailed = Object.values(results).reduce((acc, r) => acc + (r.failedCount || 0), 0);
    return { totalValid, totalInvalid, totalInserted, totalUpserted, totalFailed };
  }, [importReport]);
  // 新增：复制到剪贴板
  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      message.success('已复制到剪贴板');
    } catch (e) {
      message.error('复制失败');
    }
  };
  // 统一设备库导出文件名前缀与名称去重
  const devicePrefixTemplate = useMemo(() => {
    const val = String(exportOptions?.fileNamePrefix || '').trim();
    return val || '{name}-{date}';
  }, [exportOptions?.fileNamePrefix]);
  // 废弃的本地实现（与 utils 重复），避免遮蔽统一助手
  const buildFinalDeviceBaseName_local_removed = (baseName) => {
    const n = String(baseName || '').trim();
    const prefixResolved = resolveFilenamePrefix(devicePrefixTemplate, { name: n, project: '', line: '' });
    const prefixTrim = (prefixResolved || '').trim();
    const safeName = n
      .replace(/[\\/:*?"<>|]+/g, '')
      .replace(/\s+/g, '_')
      .slice(0, 50);
    if (!prefixTrim) return safeName || 'unnamed';
    const includesNameInPrefix = (
      prefixTrim === safeName ||
      new RegExp(`(^|[\\-_])${safeName}([\\-_]|$)`).test(prefixTrim)
    );
    return includesNameInPrefix ? prefixTrim : `${prefixTrim}-${safeName}`;
  };
  // 导出报告摘要 Markdown 生成
  const buildExportSummaryMarkdown = (report) => {
    if (!report) return '暂无导出摘要';
    const lines = [];
    lines.push('# 设备库导出摘要');
    lines.push(`- 文件: ${report.filename}`);
    lines.push(`- 导出时间: ${report.exportedAt}`);
    lines.push(`- 仅最新版本: ${report.onlyLatest ? '是' : '否'}`);
    lines.push(`- 导出表数量: ${report.tableCount ?? (report.selectedTables?.length || 0)}`);
    const total = Object.values(report.counts || {}).reduce((a, n) => a + (n || 0), 0);
    lines.push(`- 总条目数: ${total}`);
    const selected = (report.selectedTables || []).map(t => TABLE_LABELS[t] || t);
    if (selected.length) lines.push(`- 选定表: ${selected.join(', ')}`);
    lines.push('\n## 各表条目数');
    Object.keys(report.counts || {}).forEach(t => {
      lines.push(`- ${TABLE_LABELS[t] || t}: ${report.counts[t] || 0}`);
    });
    return lines.join('\n');
  };
  const buildExportSummaryMarkdownSimple = (report) => {
    if (!report) return '暂无导出摘要';
    const total = Object.values(report.counts || {}).reduce((a, n) => a + (n || 0), 0);
    const lines = [];
    lines.push('# 设备库导出摘要(精简)');
    lines.push(`- 文件: ${report.filename}`);
    lines.push(`- 总条目数: ${total}`);
    return lines.join('\n');
  };
  // 导入报告摘要 Markdown 生成
  const buildImportSummaryMarkdown = (report) => {
    if (!report) return '暂无导入摘要';
    const lines = [];
    lines.push('# 设备库导入摘要');
    const meta = report.meta || {};
    if (meta.imported_at) lines.push(`- 导入时间: ${new Date(meta.imported_at).toLocaleString('zh-CN')}`);
    if (meta.mode) lines.push(`- 导入模式: ${meta.mode}`);
    lines.push(`- 仅校验(dry run): ${meta.dryRun ? '是' : '否'}`);
    const valSummary = report.validation?.summary || [];
    const totalValid = valSummary.reduce((a, s) => a + (s.valid || 0), 0);
    const totalInvalid = valSummary.reduce((a, s) => a + (s.invalid || 0), 0);
    lines.push(`- 校验：有效 ${totalValid}，无效 ${totalInvalid}`);
    const results = report.dbResults || {};
    const inserted = Object.values(results).reduce((a, r) => a + (r.insertedCount || 0), 0);
    const upserted = Object.values(results).reduce((a, r) => a + (r.upsertedCount || 0), 0);
    const failed = Object.values(results).reduce((a, r) => a + (r.failedCount || 0), 0);
    lines.push(`- 写入：插入 ${inserted}，更新 ${upserted}，失败 ${failed}`);
    lines.push('\n## 校验各表');
    valSummary.forEach(s => {
      lines.push(`- ${TABLE_LABELS[s.table] || s.table}: 有效 ${s.valid || 0}，无效 ${s.invalid || 0}`);
    });
    lines.push('\n## 写入各表');
    Object.keys(results).forEach(t => {
      const r = results[t] || {};
      lines.push(`- ${TABLE_LABELS[t] || t}: 插入 ${r.insertedCount || 0}，更新 ${r.upsertedCount || 0}，失败 ${r.failedCount || 0}`);
    });
    return lines.join('\n');
  };
  const buildImportSummaryMarkdownSimple = (report) => {
    if (!report) return '暂无导入摘要';
    const meta = report.meta || {};
    const valSummary = report.validation?.summary || [];
    const totalValid = valSummary.reduce((a, s) => a + (s.valid || 0), 0);
    const totalInvalid = valSummary.reduce((a, s) => a + (s.invalid || 0), 0);
    const results = report.dbResults || {};
    const inserted = Object.values(results).reduce((a, r) => a + (r.insertedCount || 0), 0);
    const upserted = Object.values(results).reduce((a, r) => a + (r.upsertedCount || 0), 0);
    const failed = Object.values(results).reduce((a, r) => a + (r.failedCount || 0), 0);
    const lines = [];
    lines.push('# 设备库导入摘要(精简)');
    if (meta.mode) lines.push(`- 导入模式: ${meta.mode}`);
    lines.push(`- 仅校验(dry run): ${meta.dryRun ? '是' : '否'}`);
    lines.push(`- 校验：有效 ${totalValid}，无效 ${totalInvalid}`);
    lines.push(`- 写入：插入 ${inserted}，更新 ${upserted}，失败 ${failed}`);
    return lines.join('\n');
  };

  // CSV 构建工具
  const escapeCsvVal = (val) => {
    if (val === null || val === undefined) return '';
    const s = String(val);
    const needsQuotes = /[",\n\r]/.test(s);
    const escaped = s.replace(/"/g, '""');
    return needsQuotes ? `"${escaped}"` : escaped;
  };

  const buildExportSummaryCsv = (report) => {
    if (!report) return '';
    const lines = [];
    const total = Object.values(report.counts || {}).reduce((a, n) => a + (n || 0), 0);
    lines.push('字段,值');
    lines.push(`文件,${escapeCsvVal(report.filename)}`);
    if (report.exportedAt) lines.push(`导出时间,${escapeCsvVal(report.exportedAt)}`);
    lines.push(`仅最新版本,${report.onlyLatest ? '是' : '否'}`);
    lines.push(`导出表数量,${report.tableCount ?? (report.selectedTables?.length || 0)}`);
    lines.push(`总条目数,${total}`);
    lines.push('');
    lines.push('表,条目数');
    Object.keys(report.counts || {}).forEach(t => {
      lines.push(`${escapeCsvVal(TABLE_LABELS[t] || t)},${report.counts[t] || 0}`);
    });
    return lines.join('\n');
  };

  const buildImportSummaryCsv = (report) => {
    if (!report) return '';
    const lines = [];
    const meta = report.meta || {};
    const valSummary = report.validation?.summary || [];
    const totalValid = valSummary.reduce((a, s) => a + (s.valid || 0), 0);
    const totalInvalid = valSummary.reduce((a, s) => a + (s.invalid || 0), 0);
    const results = report.dbResults || {};
    const inserted = Object.values(results).reduce((a, r) => a + (r.insertedCount || 0), 0);
    const upserted = Object.values(results).reduce((a, r) => a + (r.upsertedCount || 0), 0);
    const failed = Object.values(results).reduce((a, r) => a + (r.failedCount || 0), 0);
    lines.push('字段,值');
    if (meta.imported_at) lines.push(`导入时间,${escapeCsvVal(new Date(meta.imported_at).toLocaleString('zh-CN'))}`);
    if (meta.mode) lines.push(`导入模式,${escapeCsvVal(meta.mode)}`);
    lines.push(`仅校验(dry run),${meta.dryRun ? '是' : '否'}`);
    lines.push(`校验有效,${totalValid}`);
    lines.push(`校验无效,${totalInvalid}`);
    lines.push(`插入,${inserted}`);
    lines.push(`更新,${upserted}`);
    lines.push(`失败,${failed}`);
    lines.push('');
    lines.push('表,有效,无效');
    valSummary.forEach(s => {
      lines.push(`${escapeCsvVal(TABLE_LABELS[s.table] || s.table)},${s.valid || 0},${s.invalid || 0}`);
    });
    lines.push('');
    lines.push('表,插入,更新,失败');
    Object.keys(results).forEach(t => {
      const r = results[t] || {};
      lines.push(`${escapeCsvVal(TABLE_LABELS[t] || t)},${r.insertedCount || 0},${r.upsertedCount || 0},${r.failedCount || 0}`);
    });
    return lines.join('\n');
  };

  const refreshTable = async (type) => {
    try {
      setLoading(prev => ({ ...prev, [type]: true }));
      let res;
      if (type === 'buses') {
        const source = onlyLatest ? 'buses_latest_vw' : 'buses';
        res = await supabase.from(source).select('*').order('name', { ascending: true }).order('version', { ascending: false });
      } else if (type === 'transformers') {
        const source = onlyLatest ? 'transformers_latest_vw' : 'transformers';
        res = await supabase.from(source).select('*').order('name', { ascending: true }).order('version', { ascending: false });
      } else if (type === 'lines') {
        const source = onlyLatest ? 'lines_latest_vw' : 'lines';
        res = await supabase.from(source).select('*').order('name', { ascending: true }).order('version', { ascending: false });
      } else if (type === 'feeders') {
        const source = onlyLatest ? 'feeders_latest_view' : 'feeders_view';
        res = await supabase.from(source).select('*').order('label', { ascending: true }).order('version', { ascending: false });
      } else if (type === 'groundTypes') {
        res = await supabase.from('ground_types').select('*').order('key', { ascending: true });
      } else if (type === 'cts') {
        const source = onlyLatest ? 'cts_latest_vw' : 'cts';
        res = await supabase.from(source).select('*').order('name', { ascending: true }).order('version', { ascending: false });
      } else if (type === 'pts') {
        const source = onlyLatest ? 'pts_latest_vw' : 'pts';
        res = await supabase.from(source).select('*').order('name', { ascending: true }).order('version', { ascending: false });
      } else if (type === 'relays') {
        const source = onlyLatest ? 'relays_latest_vw' : 'relays';
        res = await supabase.from(source).select('*').order('name', { ascending: true }).order('version', { ascending: false });
      }
      if (res?.error) throw res.error;

      const rows = res?.data || [];
      if (type === 'buses') setBuses(rows);
      else if (type === 'transformers') setTransformers(rows);
      else if (type === 'lines') setLines(rows);
      else if (type === 'feeders') setFeeders(rows);
      else if (type === 'groundTypes') setGroundTypes(rows);
      else if (type === 'cts') setCts(rows);
      else if (type === 'pts') setPts(rows);
      else if (type === 'relays') setRelays(rows);
    } catch (err) {
      console.error(err);
      message.error(`加载 ${type} 失败: ${err.message}`);
    } finally {
      setLoading(prev => ({ ...prev, [type]: false }));
    }
  };

  useEffect(() => {
    refreshTable('buses');
    refreshTable('transformers');
    refreshTable('lines');
    refreshTable('feeders');
    refreshTable('groundTypes');
    refreshTable('cts');
    refreshTable('pts');
    refreshTable('relays');
  }, []);

  const busOptions = useMemo(() => (onlyLatest ? groupLatestByName(buses) : buses).map(b => ({ value: b.id, label: `${b.name} (${b.voltage_kV}kV, v${b.version})` })), [buses, onlyLatest]);
  const transformerOptions = useMemo(() => (onlyLatest ? groupLatestByName(transformers) : transformers).map(t => ({ value: t.id, label: `${t.name} (${t.S_tr_MVA}MVA, uk ${t.uk_percent}%, ${t.vector_group}, v${t.version})` })), [transformers, onlyLatest]);
  const lineOptions = useMemo(() => (onlyLatest ? groupLatestByName(lines) : lines).map(l => ({ value: l.id, label: `${l.name} (${l.length_km}km, R ${l.R_per_km_ohm}Ω/km, X ${l.X_per_km_ohm}Ω/km, v${l.version})` })), [lines, onlyLatest]);
  const ctOptions = useMemo(() => (onlyLatest ? groupLatestByName(cts) : cts).map(c => ({ value: c.id, label: `${c.name} (${c.ratio ?? c.ct_ratio ?? ''}:1, v${c.version})` })), [cts, onlyLatest]);
  const ptOptions = useMemo(() => (onlyLatest ? groupLatestByName(pts) : pts).map(p => ({ value: p.id, label: `${p.name} (${p.ratio ?? p.pt_ratio ?? ''}:1, v${p.version})` })), [pts, onlyLatest]);
  const relayOptions = useMemo(() => (onlyLatest ? groupLatestByName(relays) : relays).map(r => ({ value: r.id, label: `${r.name || r.model || r.id} (v${r.version})` })), [relays, onlyLatest]);

  const openAdd = (type) => {
    setModalType(type);
    setEditingRecord(null);
    form.resetFields();
    setModalOpen(true);
  };

  const openNewVersion = (type, record) => {
    setModalType(type);
    setEditingRecord(record);
    // prefill form
    if (type === 'buses') {
      form.setFieldsValue({ name: record.name, voltage_kV: record.voltage_kV, version: (record.version || 0) + 1, status: 'active' });
    } else if (type === 'transformers') {
      form.setFieldsValue({ name: record.name, S_tr_MVA: record.S_tr_MVA, uk_percent: record.uk_percent, vector_group: record.vector_group, secondary_neutral_ground: record.secondary_neutral_ground, version: (record.version || 0) + 1, status: 'active' });
    } else if (type === 'lines') {
      form.setFieldsValue({ name: record.name, R_per_km_ohm: record.R_per_km_ohm, X_per_km_ohm: record.X_per_km_ohm, length_km: record.length_km, z0_factor: record.z0_factor, version: (record.version || 0) + 1, status: 'active' });
    } else if (type === 'feeders') {
      form.setFieldsValue({ label: record.label, name: record.name, bus_id: record.bus_id, transformer_id: record.transformer_id, line_id: record.line_id, ct_id: record.ct_id, pt_id: record.pt_id, relay_id: record.relay_id, rated_current: record.rated_current, ct_ratio: record.ct_ratio, source_S_sc_MVA: record.source_S_sc_MVA, source_X_R: record.source_X_R, ground_R_ohm: record.ground_R_ohm, ground_X_ohm: record.ground_X_ohm, version: (record.version || 0) + 1, status: 'active' });
    } else if (type === 'groundTypes') {
      form.setFieldsValue({ key: record.key, label: record.label, default_R_ohm: record.default_R_ohm, default_X_ohm: record.default_X_ohm });
    } else if (type === 'cts') {
      form.setFieldsValue({ name: record.name, ratio: record.ratio ?? record.ct_ratio, version: (record.version || 0) + 1, status: 'active' });
    } else if (type === 'pts') {
      form.setFieldsValue({ name: record.name, ratio: record.ratio ?? record.pt_ratio, version: (record.version || 0) + 1, status: 'active' });
    } else if (type === 'relays') {
      form.setFieldsValue({ name: record.name, model: record.model, version: (record.version || 0) + 1, status: 'active' });
    }
    setModalOpen(true);
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      if (modalType === 'buses') {
        const payload = { name: values.name, voltage_kV: values.voltage_kV, version: values.version || 1, status: values.status || 'active' };
        const { error } = await supabase.from('buses').insert(payload);
        if (error) throw error;
        message.success('母线已保存');
        refreshTable('buses');
      } else if (modalType === 'transformers') {
        const payload = { name: values.name, S_tr_MVA: values.S_tr_MVA, uk_percent: values.uk_percent, vector_group: values.vector_group, secondary_neutral_ground: values.secondary_neutral_ground, version: values.version || 1, status: values.status || 'active' };
        const { error } = await supabase.from('transformers').insert(payload);
        if (error) throw error;
        message.success('变压器已保存');
        refreshTable('transformers');
      } else if (modalType === 'lines') {
        const payload = { name: values.name, R_per_km_ohm: values.R_per_km_ohm, X_per_km_ohm: values.X_per_km_ohm, length_km: values.length_km, z0_factor: values.z0_factor, version: values.version || 1, status: values.status || 'active' };
        const { error } = await supabase.from('lines').insert(payload);
        if (error) throw error;
        message.success('线路已保存');
        refreshTable('lines');
      } else if (modalType === 'feeders') {
        const payload = { label: values.label, name: values.name, bus_id: values.bus_id, transformer_id: values.transformer_id, line_id: values.line_id, ct_id: values.ct_id, pt_id: values.pt_id, relay_id: values.relay_id, rated_current: values.rated_current, ct_ratio: values.ct_ratio, source_S_sc_MVA: values.source_S_sc_MVA, source_X_R: values.source_X_R, ground_R_ohm: values.ground_R_ohm, ground_X_ohm: values.ground_X_ohm, version: values.version || 1, status: values.status || 'active' };
        const { error } = await supabase.from('feeders').insert(payload);
        if (error) throw error;
        message.success('馈线已保存');
        refreshTable('feeders');
      } else if (modalType === 'groundTypes') {
        // ground_types use key as id; allow update defaults via upsert
        const payload = { key: values.key, label: values.label, default_R_ohm: values.default_R_ohm, default_X_ohm: values.default_X_ohm };
        const { error } = await supabase.from('ground_types').upsert(payload, { onConflict: 'key' });
        if (error) throw error;
        message.success('接地类型模板已保存');
        refreshTable('groundTypes');
      } else if (modalType === 'cts') {
        const payload = { name: values.name, ratio: values.ratio, version: values.version || 1, status: values.status || 'active' };
        const { error } = await supabase.from('cts').insert(payload);
        if (error) throw error;
        message.success('CT 已保存');
        refreshTable('cts');
      } else if (modalType === 'pts') {
        const payload = { name: values.name, ratio: values.ratio, version: values.version || 1, status: values.status || 'active' };
        const { error } = await supabase.from('pts').insert(payload);
        if (error) throw error;
        message.success('PT 已保存');
        refreshTable('pts');
      } else if (modalType === 'relays') {
        const payload = { name: values.name, model: values.model, version: values.version || 1, status: values.status || 'active' };
        const { error } = await supabase.from('relays').insert(payload);
        if (error) throw error;
        message.success('继电器已保存');
        refreshTable('relays');
      }
      setModalOpen(false);
      setEditingRecord(null);
      form.resetFields();
    } catch (err) {
      if (err?.message) message.error(err.message); else message.error('保存失败');
    }
  };

  // 导出/导入处理
  const handleConfirmExport = async () => {
    try {
      const res = await exportDeviceLibraryToJson({ onlyLatest: exportOptions.onlyLatest, tables: exportOptions.tables, fileNamePrefix: exportOptions.fileNamePrefix });
      message.success(`导出成功：${res.filename}`);
      setExportOptionsOpen(false);
      setExportReport({
        filename: res.filename,
        counts: res.counts || {},
        tableCount: res.tableCount,
        onlyLatest: res.onlyLatest,
        selectedTables: exportOptions.tables,
        fileNamePrefix: exportOptions.fileNamePrefix,
        exportedAt: new Date().toLocaleString('zh-CN')
      });
      setExportReportOpen(true);
    } catch (err) {
      message.error(err?.message || '导出失败');
    }
  };

  const handleExportDevices = () => {
    setExportOptionsOpen(true);
  };

  const handleImportDevices = async () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.json,application/json';
    input.onchange = async () => {
      const file = input.files?.[0];
      if (!file) return;
      try {
        const text = await file.text();
        const report = await importDeviceLibraryFromJson(text, { mode: 'upsert' });
        setImportReport(report);
        setImportReportOpen(true);
        const valSummary = report?.validation?.summary || [];
        const totalValid = valSummary.reduce((acc, s) => acc + (s.valid || 0), 0);
        const totalInvalid = valSummary.reduce((acc, s) => acc + (s.invalid || 0), 0);
        message.success(`导入完成：有效 ${totalValid}，无效 ${totalInvalid}`);
        // 刷新所有表
        await Promise.all([
          refreshTable('buses'),
          refreshTable('transformers'),
          refreshTable('lines'),
          refreshTable('feeders'),
          refreshTable('groundTypes'),
          refreshTable('cts'),
          refreshTable('pts'),
          refreshTable('relays')
        ]);
      } catch (err) {
        console.error(err);
        message.error(err?.message || '导入失败');
      }
    };
    input.click();
  };


  // 导入选项：选择文件
  const handlePickImportFile = async () => {
  const input = document.createElement('input');
  input.type = 'file';
  input.accept = '.json,application/json';
  input.onchange = async () => {
  const file = input.files?.[0];
  if (!file) return;
  try {
  const text = await file.text();
  setImportOptions(prev => ({ ...prev, fileName: file.name, fileText: text }));
  message.success(`已选择文件：${file.name}`);
  } catch (err) {
  message.error(err?.message || '读取文件失败');
  }
  };
  input.click();
  };
  
  // 导入选项：执行导入或干跑
  const handleConfirmImport = async () => {
  if (!importOptions.fileText) {
  message.error('请先选择 JSON 文件');
  return;
  }
  try {
  const report = await importDeviceLibraryFromJson(importOptions.fileText, { mode: importOptions.mode, dryRun: importOptions.dryRun });
  setImportReport(report);
  setImportReportOpen(true);
  const valSummary = report?.validation?.summary || [];
  const totalValid = valSummary.reduce((acc, s) => acc + (s.valid || 0), 0);
  const totalInvalid = valSummary.reduce((acc, s) => acc + (s.invalid || 0), 0);
  message.success(`${importOptions.dryRun ? '校验完成' : '导入完成'}：有效 ${totalValid}，无效 ${totalInvalid}`);
  if (!importOptions.dryRun) {
  await Promise.all([
  refreshTable('buses'),
  refreshTable('transformers'),
  refreshTable('lines'),
  refreshTable('feeders'),
  refreshTable('groundTypes'),
  refreshTable('cts'),
  refreshTable('pts'),
  refreshTable('relays')
  ]);
  }
  setImportOptionsOpen(false);
  } catch (err) {
  console.error(err);
  message.error(err?.message || '导入失败');
  }
  };

  // ===== 厂商曲线导入与解析 =====
  const openVendorImport = () => {
    setVendorImportOpen(true);
  };

  const handlePickVendorFile = async () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.json,.csv,application/json,text/csv';
    input.onchange = async () => {
      const file = input.files?.[0];
      if (!file) return;
      try {
        const text = await file.text();
        setVendorImportFileName(file.name);
        setVendorImportText(text);
        message.success(`已选择文件：${file.name}`);
      } catch (err) {
        message.error(err?.message || '读取文件失败');
      }
    };
    input.click();
  };

  // 统一曲线类型映射由 tccUtils 提供（兼容 IEC/IEEE 与厂商别名）
  // 已在解析处直接使用 normalizeCurveType

  const parseVendorCurvesFromText = (text) => {
    const records = [];
    const errors = [];
    if (!text || !text.trim()) return { records, errors: ['空文件'] };
    // 单位归一化工具由 tccUtils 提供
    // 尝试 JSON
    try {
      const obj = JSON.parse(text);
      const arr = Array.isArray(obj) ? obj : (Array.isArray(obj?.curves) ? obj.curves : [obj]);
      arr.filter(Boolean).forEach((item, idx) => {
        try {
          // 支持两种结构：1) { results: {...} } 2) 平铺字段
          const res = item.results || {};
          const axes = res.axesType || item.axesType || 'I-t';
          const units = item.units || res.units || {};
          const currentUnit = units.current || units.i || item.currentUnit || res.currentUnit;
          const timeUnit = units.time || units.t || item.timeUnit || res.timeUnit;
          const ctRatioUnit = units.ctRatio ?? item.ctRatio ?? res.ctRatio ?? item.ct_ratio ?? res.ct_ratio;
          const pickupRaw = res.pickupCurrent ?? item.pickupCurrent ?? item.pickup;
          const pickup = normalizeCurrent(pickupRaw, currentUnit, ctRatioUnit);
          const TMS = toNumber(res.timeMultiplier ?? item.timeMultiplier ?? item.TMS);
          const curveType = normalizeCurveType(res.curveType ?? item.curveType);
          const label = item.label || item.name || `厂商曲线${idx + 1}`;
          const color = item.color || res.color || undefined;
          const results = { axesType: axes, pickupCurrent: pickup, timeMultiplier: TMS, curveType };
          // 透传可选结构：Z/差动特性
          if (Array.isArray(item.zones)) results.zones = item.zones;
          if (item.characteristic) results.characteristic = item.characteristic;
          if (typeof item.lineAngleDeg === 'number') results.lineAngleDeg = item.lineAngleDeg;
          // 透传二次谐波闭锁系数 Kb
          if (typeof res.secondHarmonicBlocking === 'number') results.secondHarmonicBlocking = res.secondHarmonicBlocking;
          if (typeof item.secondHarmonicBlocking === 'number') results.secondHarmonicBlocking = item.secondHarmonicBlocking;
          // 兼容 M-t 点
          if (Array.isArray(res.curvePoints)) results.curvePoints = res.curvePoints;
          if (Array.isArray(item.curvePoints)) results.curvePoints = item.curvePoints;
          if (Array.isArray(results.curvePoints)) {
            results.curvePoints = results.curvePoints.map((pt) => {
              if (Array.isArray(pt)) {
                const M = toNumber(pt[0]);
                const t = normalizeTime(pt[1], timeUnit);
                return [M, t];
              } else {
                const M = toNumber(pt.M);
                const t = normalizeTime(pt.t, timeUnit);
                return { M, t };
              }
            }).filter((pt) => {
              const t = Array.isArray(pt) ? pt[1] : pt.t;
              return isFinite(t) && t >= 0;
            });
          }
          // 校验基本必填
          const hasI2t = axes === 'I-t' && pickup && TMS && curveType;
          const hasZ = axes === 'Z' && Array.isArray(results.zones) && results.zones.length > 0;
          const hasDiff = axes === 'Id-Ir' && !!results.characteristic;
          if (!(hasI2t || hasZ || hasDiff)) {
            errors.push(`${label}: 缺少必要参数，已跳过`);
            return;
          }
          // 边界校核（软性）：二次侧拾取电流过大与 TMS 范围
          if (axes === 'I-t') {
            if (!isFinite(pickup) || pickup <= 0) {
              errors.push(`${label}: 拾取电流无效，已跳过`);
              return;
            }
            if (!isFinite(TMS) || TMS <= 0) {
              errors.push(`${label}: 时间倍数(TMS)无效，已跳过`);
              return;
            }
            if (pickup > 50) {
              // 常见 1A/5A 二次侧拾取电流不应超过几十安培，这里做提示但不阻断
              console.warn(`[厂商曲线] ${label} 的拾取电流(${pickup}A)异常偏大，请确认单位与CT变比`);
            }
          }
          records.push({ id: `${label}_${idx}`, result: { name: label }, results, meta: color ? { color } : {} });
        } catch (e) {
          errors.push(`记录${idx + 1} 解析失败: ${e?.message || '未知错误'}`);
        }
      });
      return { records, errors };
    } catch (_) {
      // 退回 CSV：要求列 label,curveType,timeMultiplier,pickupCurrent[,color][,axesType]
      try {
        const lines = text.split(/\r?\n/).filter(l => l.trim().length > 0);
        if (lines.length === 0) throw new Error('CSV 为空');
        const header = lines[0].split(',').map(s => s.trim());
        const idxLabel = header.indexOf('label');
        const idxCurve = header.indexOf('curveType');
        const idxTms = header.indexOf('timeMultiplier');
        const idxPickup = header.indexOf('pickupCurrent');
        const idxColor = header.indexOf('color');
        const idxAxes = header.indexOf('axesType');
        const idxCt = header.indexOf('ctRatio');
        const idxCurUnit = header.indexOf('currentUnit');
        const idxTimeUnit = header.indexOf('timeUnit');
        const idxKb = (header.indexOf('Kb') >= 0 ? header.indexOf('Kb') : header.indexOf('secondHarmonicBlocking'));
        if (idxLabel < 0 || idxCurve < 0 || idxTms < 0 || idxPickup < 0) {
          throw new Error('CSV 缺少必要列');
        }
        for (let i = 1; i < lines.length; i++) {
          const cols = lines[i].split(',');
          const label = (cols[idxLabel] || '').trim() || `厂商曲线${i}`;
          const curveType = normalizeVendorCurveType((cols[idxCurve] || '').trim());
          const TMS = toNumber(cols[idxTms]);
          const ct = idxCt >= 0 ? toNumber(cols[idxCt]) : undefined;
          const curUnit = idxCurUnit >= 0 ? (cols[idxCurUnit] || '').trim() : undefined;
          const timeUnit = idxTimeUnit >= 0 ? (cols[idxTimeUnit] || '').trim() : undefined;
          const pickup = normalizeCurrent(cols[idxPickup], curUnit, ct);
          const color = idxColor >= 0 ? (cols[idxColor] || '').trim() : undefined;
          const axes = idxAxes >= 0 ? (cols[idxAxes] || '').trim() : 'I-t';
          const kbRaw = (idxKb != null && idxKb >= 0) ? (cols[idxKb] || '').trim() : '';
          const kb = kbRaw !== '' ? Number(kbRaw) : undefined;
          if (!pickup || !TMS || !curveType) {
            errors.push(`${label}: 参数不完整，已跳过`);
            continue;
          }
          const results = { axesType: axes || 'I-t', pickupCurrent: pickup, timeMultiplier: TMS, curveType };
          if (typeof kb === 'number' && isFinite(kb)) results.secondHarmonicBlocking = kb;
          // CSV 可选：M-t 点，若存在列 M,t,timeUnit 则按行解析（宽表不处理）
          records.push({ id: `${label}_${i - 1}`, result: { name: label }, results, meta: color ? { color } : {} });
        }
        return { records, errors };
      } catch (e2) {
        return { records: [], errors: [e2?.message || '无法解析文件'] };
      }
    }
  };

  const handleParseVendorCurves = () => {
    const { records, errors } = parseVendorCurvesFromText(vendorImportText);
    const withUnified = vendorUseUnifiedColor ? records.map(r => ({ ...r, meta: { ...(r.meta || {}), color: vendorUnifiedColor } })) : records;
    setVendorParsedRecords(withUnified);
    setVendorParseErrors(errors || []);
    if (records.length) message.success(`解析成功：${records.length} 条曲线`);
    if (errors?.length) message.warning(`部分解析失败：${errors.length}`);
  };

  const handleAddVendorCurvesToOverlay = () => {
    if (!vendorParsedRecords || vendorParsedRecords.length === 0) {
      message.warning('请先解析并确认曲线');
      return;
    }
    setOverlayRecords(prev => ([...(prev || []), ...vendorParsedRecords]));
    setVendorImportOpen(false);
    setOverlayOpen(true);
  };

  const handleDownloadTemplate = async () => {
    try {
      await downloadDeviceLibraryTemplateJson();
      message.success('模板已下载');
    } catch (err) {
      message.error(err?.message || '模板下载失败');
    }
  };

  const downloadJson = (obj, filename = 'import-report.json') => {
    try {
      const blob = new Blob([JSON.stringify(obj, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error(e);
      message.error('报告下载失败');
    }
  };

  // 叠加校核：选择与配置
  const [selectedFeederRowKeys, setSelectedFeederRowKeys] = useState([]);
  const [selectedFeederRows, setSelectedFeederRows] = useState([]);
  const [overlayOpen, setOverlayOpen] = useState(false);
  const [overlayRecords, setOverlayRecords] = useState([]);
  const overlayRef = useRef(null);
  const [overlaySettingsOpen, setOverlaySettingsOpen] = useState(false);
  const [overlaySettings, setOverlaySettings] = useState(() => {
    try {
      const saved = localStorage.getItem('overlaySettings');
      return saved ? JSON.parse(saved) : { sortMode: 'byRatedCurrent', filenamePrefix: '', minMargin: 0.3 };
    } catch { return { sortMode: 'byRatedCurrent', filenamePrefix: '', minMargin: 0.3 }; }
  });
  const [selectedFeederOrder, setSelectedFeederOrder] = useState(() => {
    try {
      const saved = localStorage.getItem('selectedFeederOrder');
      return saved ? JSON.parse(saved) : [];
    } catch { return []; }
  });
  const [overlayOptionsOpen, setOverlayOptionsOpen] = useState(false);
  const [overlayOptions, setOverlayOptions] = useState({
    protectionType: 'overcurrent',
    curveType: 'Normal Inverse',
    timeMultiplier: 0.5,
    faultType: '3P', // 3P, LL, 1P-G
    scenario: 'max', // max or min
    polarizingAngle: 30,
    k0: 0.3,
    overridePtRatio: undefined
  });
  useEffect(() => {
    try { localStorage.setItem('overlayOptions', JSON.stringify(overlayOptions)); } catch {}
  }, [overlayOptions]);
  useEffect(() => {
    try { localStorage.setItem('overlaySettings', JSON.stringify(overlaySettings)); } catch {}
  }, [overlaySettings]);
  useEffect(() => {
    try { localStorage.setItem('selectedFeederOrder', JSON.stringify(selectedFeederOrder)); } catch {}
  }, [selectedFeederOrder]);

  const handleOpenOverlayOptions = () => {
    if ((selectedFeederRowKeys || []).length < 2) {
      message.warning('请至少选择两条馈线进行叠加校核');
      return;
    }
    setOverlayOptionsOpen(true);
  };

  const buildOverlayRecords = async () => {
    const ids = selectedFeederRowKeys;
    const rows = selectedFeederRows;
    if (!ids || ids.length < 2) {
      message.warning('请至少选择两条馈线');
      return [];
    }
    const results = [];
    const errors = [];
    // 排序：按设置选择（默认按额定电流升序；或按选中顺序，第一个为下级）
    let sorted = [];
    if (overlaySettings.sortMode === 'bySelectionOrder') {
      const map = new Map((rows || []).map(r => [r.id, r]));
      sorted = (selectedFeederOrder || []).map(k => map.get(k)).filter(Boolean);
      if (!sorted.length) sorted = [...rows];
    } else {
      sorted = [...rows].sort((a, b) => (Number(a.rated_current) || 0) - (Number(b.rated_current) || 0));
    }
    for (const r of sorted) {
      try {
        const f = await fetchFeederByIdAsync(r.id);
        if (!f) { errors.push(`${r.label || r.name || r.id}: 未找到详情`); continue; }
        const Un_kV = f?.bus?.voltage_kV || r.bus_voltage_kV || 0;
        const sc = calculateShortCircuitIEC60909Multi({
          Un_kV,
          source: { S_sc_MVA: f?.source?.S_sc_MVA, X_R: f?.source?.X_R },
          transformer: { S_tr_MVA: f?.transformer?.S_tr_MVA, uk_percent: f?.transformer?.uk_percent, vector_group: f?.transformer?.vector_group, secondary_neutral_ground: f?.transformer?.secondary_neutral_ground },
          line: { R_per_km_ohm: f?.line?.R_per_km_ohm, X_per_km_ohm: f?.line?.X_per_km_ohm, length_km: f?.line?.length_km },
          ground_R_ohm: f?.ground?.R_ohm,
          ground_X_ohm: f?.ground?.X_ohm,
          z0_factor: f?.line?.z0_factor
        });
        let faultCurrent = 0;
        if (overlayOptions.faultType === '3P') {
          faultCurrent = overlayOptions.scenario === 'min' ? (sc?.three_phase?.Ik3p_min_A || 0) : (sc?.three_phase?.Ik3p_max_A || 0);
        } else if (overlayOptions.faultType === 'LL') {
          faultCurrent = sc?.two_phase_ll?.Ik_ll_max_A || 0;
        } else if (overlayOptions.faultType === '1P-G') {
          faultCurrent = sc?.one_phase_earth?.Ik_1phg_max_A || 0;
        }

        // 构造保护计算输入
        const valuesBase = {
          protectionType: overlayOptions.protectionType,
          ratedCurrent: f?.ratedCurrent || r.rated_current,
          ctRatio: f?.ctRatio || r.ct_ratio,
          faultCurrent,
          curveType: overlayOptions.curveType,
          timeMultiplier: overlayOptions.timeMultiplier,
        };
        let values = { ...valuesBase };
        if (overlayOptions.protectionType === 'distance' || overlayOptions.protectionType === 'directional') {
          values.systemVoltage = Un_kV;
          values.ptRatio = typeof overlayOptions.overridePtRatio === 'number' && overlayOptions.overridePtRatio > 0
            ? overlayOptions.overridePtRatio
            : (f?.ptRatio || r.pt_ratio || 1);
        }
        if (overlayOptions.protectionType === 'distance') {
          const R_line = (f?.line?.R_per_km_ohm || r.R_per_km_ohm || 0) * (f?.line?.length_km || r.length_km || 0);
          const X_line = (f?.line?.X_per_km_ohm || r.X_per_km_ohm || 0) * (f?.line?.length_km || r.length_km || 0);
          values.lineR_ohm = R_line;
          values.lineX_ohm = X_line;
          values.selectedFeederId = f.id;
        }
        if (overlayOptions.protectionType === 'directional') {
          values.polarizingAngle = overlayOptions.polarizingAngle;
        }
        if (overlayOptions.protectionType === 'zero_sequence') {
          values.k0 = overlayOptions.k0;
        }
        if (overlayOptions.protectionType === 'differential') {
          values.transformerCapacity = f?.transformer?.S_tr_MVA || r.S_tr_MVA || 0;
        }

        const vres = validateInputs(values);
        if (!vres.valid) {
          errors.push(`${r.label || r.name || r.id}: ${vres.errors.join('；')}`);
          continue;
        }
        const calc = calculateProtectionSettings(values);
        const selectionOrderIds = Array.isArray(selectedFeederOrder) ? selectedFeederOrder : [];
        const idToLabel = new Map((selectedFeederRows || []).map(rr => [rr.id, (rr.label || rr.name || rr.id)]));
        const selectionOrderLabels = selectionOrderIds.map(id => idToLabel.get(id)).filter(Boolean);
        const meta = {
          sortMode: overlaySettings?.sortMode,
          ratedCurrent: values.ratedCurrent,
          ctRatio: values.ctRatio,
          ptRatio: values.ptRatio,
          lineR_ohm: values.lineR_ohm,
          lineX_ohm: values.lineX_ohm,
          systemVoltage_kV: values.systemVoltage,
          selectionOrderIds,
          selectionOrderLabels,
        };
        results.push({ id: f.id, result: { name: f.label || f.name || f.id }, results: calc, meta });
      } catch (e) {
        errors.push(`${r.label || r.name || r.id}: ${e?.message || '计算失败'}`);
      }
    }
    if (errors.length) {
      message.warning(`部分记录无法参与叠加：${errors.length}，详情见控制台`);
      // eslint-disable-next-line no-console
      console.warn('[设备库叠加校核] 跳过记录：', errors);
    }
    return results;
  };

  const handleComputeOverlayAndOpen = async () => {
    try {
      const recs = await buildOverlayRecords();
      setOverlayRecords(recs);
      setOverlayOpen(true);
      setOverlayOptionsOpen(false);
    } catch (e) {
      message.error(e?.message || '叠加校核初始化失败');
    }
  };

  // 从工具栏直接导出叠加 PNG
  const handleExportOverlayPngFromToolbar = async () => {
    try {
      if ((selectedFeederRowKeys || []).length < 2) {
        message.warning('请至少选择两条馈线进行导出');
        return;
      }
      const recs = await buildOverlayRecords();
      if (!recs || recs.length < 2) {
        message.warning('可参与叠加的曲线不足两条，无法导出');
        return;
      }
      setOverlayRecords(recs);
      setOverlayOpen(true);
      // 等待模态与图形渲染完成后再捕获图片
      setTimeout(() => {
        try { overlayRef.current?.exportPng(); } catch (_) { /* 忽略 */ }
      }, 500);
    } catch (e) {
      message.error(e?.message || '导出叠加图失败');
    }
  };

  // 从工具栏直接导出校核 Markdown
  const handleExportOverlayMdFromToolbar = async () => {
    try {
      if ((selectedFeederRowKeys || []).length < 2) {
        message.warning('请至少选择两条馈线进行导出');
        return;
      }
      const recs = await buildOverlayRecords();
      if (!recs || recs.length < 2) {
        message.warning('可参与叠加的曲线不足两条，无法导出');
        return;
      }
      setOverlayRecords(recs);
      setOverlayOpen(true);
      // 等待模态渲染并完成分析后导出 Markdown
      setTimeout(() => {
        try { overlayRef.current?.exportMd(); } catch (_) { /* 忽略 */ }
      }, 600);
    } catch (e) {
      message.error(e?.message || '导出校核结果失败');
    }
  };

  // 通用列：名称、版本、状态
  const colCommon = [
    { title: '名称', dataIndex: 'name', key: 'name', width: 180 },
    { title: '版本', dataIndex: 'version', key: 'version', width: 100 },
    { title: '状态', dataIndex: 'status', key: 'status', width: 120, render: (v) => <Tag color={v === 'active' ? 'green' : 'default'}>{v}</Tag> },
  ];

  const busColumns = [
    { title: '名称', dataIndex: 'name', key: 'name', width: 180 },
    { title: '电压(kV)', dataIndex: 'voltage_kV', key: 'voltage_kV', width: 120 },
    { title: '版本', dataIndex: 'version', key: 'version', width: 100 },
    { title: '状态', dataIndex: 'status', key: 'status', width: 120, render: (v) => <Tag color={v === 'active' ? 'green' : 'default'}>{v}</Tag> },
    { title: '操作', key: 'actions', width: 160, render: (_, r) => (
      <Space>
        <Button onClick={() => openNewVersion('buses', r)}>新版本</Button>
      </Space>
    )}
  ];

  const transformerColumns = [
    { title: '名称', dataIndex: 'name', key: 'name', width: 180 },
    { title: '容量(MVA)', dataIndex: 'S_tr_MVA', key: 'S_tr_MVA', width: 140 },
    { title: 'uk(%)', dataIndex: 'uk_percent', key: 'uk_percent', width: 100 },
    { title: '向量组', dataIndex: 'vector_group', key: 'vector_group', width: 120 },
    { title: '版本', dataIndex: 'version', key: 'version', width: 100 },
    { title: '状态', dataIndex: 'status', key: 'status', width: 120, render: (v) => <Tag color={v === 'active' ? 'green' : 'default'}>{v}</Tag> },
    { title: '操作', key: 'actions', width: 160, render: (_, r) => (
      <Space>
        <Button onClick={() => openNewVersion('transformers', r)}>新版本</Button>
      </Space>
    )}
  ];

  const lineColumns = [
    { title: '名称', dataIndex: 'name', key: 'name', width: 180 },
    { title: '长度(km)', dataIndex: 'length_km', key: 'length_km', width: 120 },
    { title: 'R(Ω/km)', dataIndex: 'R_per_km_ohm', key: 'R_per_km_ohm', width: 120 },
    { title: 'X(Ω/km)', dataIndex: 'X_per_km_ohm', key: 'X_per_km_ohm', width: 120 },
    { title: '零序系数', dataIndex: 'z0_factor', key: 'z0_factor', width: 120 },
    { title: '版本', dataIndex: 'version', key: 'version', width: 100 },
    { title: '状态', dataIndex: 'status', key: 'status', width: 120, render: (v) => <Tag color={v === 'active' ? 'green' : 'default'}>{v}</Tag> },
    { title: '操作', key: 'actions', width: 160, render: (_, r) => (
      <Space>
        <Button onClick={() => openNewVersion('lines', r)}>新版本</Button>
      </Space>
    )}
  ];

  const feederColumns = [
    { title: '标识', dataIndex: 'label', key: 'label', width: 140 },
    { title: '名称', dataIndex: 'name', key: 'name', width: 180 },
    { title: '母线', dataIndex: 'bus_name', key: 'bus_name', width: 160 },
    { title: '变压器', dataIndex: 'transformer_name', key: 'transformer_name', width: 160 },
    { title: '线路', dataIndex: 'line_name', key: 'line_name', width: 180 },
    { title: 'CT', dataIndex: 'ct_name', key: 'ct_name', width: 160 },
    { title: 'PT', dataIndex: 'pt_name', key: 'pt_name', width: 160 },
    { title: '继电器', dataIndex: 'relay_name', key: 'relay_name', width: 160 },
    { title: '额定电流(A)', dataIndex: 'rated_current', key: 'rated_current', width: 160 },
    { title: 'CT变比', dataIndex: 'ct_ratio', key: 'ct_ratio', width: 120 },
    { title: '接地R(Ω)', dataIndex: 'ground_R_ohm', key: 'ground_R_ohm', width: 120 },
    { title: '接地X(Ω)', dataIndex: 'ground_X_ohm', key: 'ground_X_ohm', width: 120 },
    { title: '更新时间', dataIndex: 'updated_at', key: 'updated_at', width: 180 },
    { title: '操作', key: 'actions', width: 200, fixed: 'right', render: (_, r) => (
      <Space>
        <Button onClick={() => openNewVersion('feeders', r)}>新版本</Button>
      </Space>
    )}
  ];

  const groundTypeColumns = [
    { title: 'Key', dataIndex: 'key', key: 'key', width: 140 },
    { title: '名称', dataIndex: 'label', key: 'label' },
    { title: '默认R(Ω)', dataIndex: 'default_R_ohm', key: 'default_R_ohm', width: 140 },
    { title: '默认X(Ω)', dataIndex: 'default_X_ohm', key: 'default_X_ohm', width: 140 },
    { title: '操作', key: 'actions', width: 160, render: (_, r) => (
      <Space>
        <Button onClick={() => openNewVersion('groundTypes', r)}>编辑</Button>
      </Space>
    )}
  ];

  const tabItems = [
    {
      key: 'feeders',
      label: '馈线',
      children: (
        <div>
          <LatestToggle checked={onlyLatest} onChange={setOnlyLatest} />
          <Space style={{ marginBottom: 12 }}>
            <Button type="primary" onClick={() => openAdd('feeders')}>新增馈线</Button>
            <Button onClick={() => refreshTable('feeders')}>刷新</Button>
             <Button onClick={handleExportDevices}>导出设备库</Button>
              <Button onClick={() => setImportOptionsOpen(true)}>导入设备库</Button>
             <Button onClick={handleDownloadTemplate}>下载模板</Button>
             <Button data-testid="toolbar-vendor-import-open" onClick={openVendorImport}>导入厂商曲线</Button>
             <Button data-testid="toolbar-overlay-open" type="primary" disabled={(selectedFeederRowKeys || []).length < 2} onClick={handleOpenOverlayOptions}>叠加校核</Button>
             <Button data-testid="toolbar-overlay-export-png" disabled={(selectedFeederRowKeys || []).length < 2} onClick={handleExportOverlayPngFromToolbar}>导出叠加PNG</Button>
             <Button data-testid="toolbar-overlay-export-md" disabled={(selectedFeederRowKeys || []).length < 2} onClick={handleExportOverlayMdFromToolbar}>导出校核Markdown</Button>
             <Button data-testid="toolbar-overlay-settings-open" onClick={() => setOverlaySettingsOpen(true)}>叠加设置</Button>
          </Space>
          <Table
            rowKey="id"
            loading={loading.feeders}
            dataSource={(onlyLatest ? groupLatestByName(feeders) : feeders)}
            columns={feederColumns}
            scroll={{ x: 1300 }}
            rowSelection={{
              selectedRowKeys: selectedFeederRowKeys,
              onChange: (keys, rows) => {
                setSelectedFeederRowKeys(keys);
                setSelectedFeederRows(rows);
                if (overlaySettings.sortMode !== 'bySelectionOrder' || !selectedFeederOrder.length) {
                  setSelectedFeederOrder(keys);
                }
              },
              onSelect: (record, selected) => {
                setSelectedFeederOrder(prev => {
                  const next = prev.filter(k => k !== record.id);
                  if (selected) next.push(record.id);
                  return next;
                });
              }
            }}
          />
        </div>
      )
    },
    {
      key: 'buses',
      label: '母线',
      children: (
        <div>
          <LatestToggle checked={onlyLatest} onChange={setOnlyLatest} />
          <Space style={{ marginBottom: 12 }}>
            <Button type="primary" onClick={() => openAdd('buses')}>新增母线</Button>
            <Button onClick={() => refreshTable('buses')}>刷新</Button>
          </Space>
          <Table rowKey="id" loading={loading.buses} dataSource={(onlyLatest ? groupLatestByName(buses) : buses)} columns={busColumns} />
        </div>
      )
    },
    {
      key: 'transformers',
      label: '变压器',
      children: (
        <div>
          <LatestToggle checked={onlyLatest} onChange={setOnlyLatest} />
          <Space style={{ marginBottom: 12 }}>
            <Button type="primary" onClick={() => openAdd('transformers')}>新增变压器</Button>
            <Button onClick={() => refreshTable('transformers')}>刷新</Button>
          </Space>
          <Table rowKey="id" loading={loading.transformers} dataSource={(onlyLatest ? groupLatestByName(transformers) : transformers)} columns={transformerColumns} />
        </div>
      )
    },
    {
      key: 'lines',
      label: '线路',
      children: (
        <div>
          <LatestToggle checked={onlyLatest} onChange={setOnlyLatest} />
          <Space style={{ marginBottom: 12 }}>
            <Button type="primary" onClick={() => openAdd('lines')}>新增线路</Button>
            <Button onClick={() => refreshTable('lines')}>刷新</Button>
          </Space>
          <Table rowKey="id" loading={loading.lines} dataSource={(onlyLatest ? groupLatestByName(lines) : lines)} columns={lineColumns} />
        </div>
      )
    },
    {
      key: 'cts',
      label: 'CT',
      children: (
        <div>
          <LatestToggle checked={onlyLatest} onChange={setOnlyLatest} />
          <Space style={{ marginBottom: 12 }}>
            <Button type="primary" onClick={() => openAdd('cts')}>新增 CT</Button>
            <Button onClick={() => refreshTable('cts')}>刷新</Button>
          </Space>
          <Table
            rowKey="id"
            loading={loading.cts}
            dataSource={(onlyLatest ? groupLatestByName(cts) : cts)}
            columns={[
              ...colCommon,
              { title: '变比', dataIndex: 'ratio', key: 'ratio', width: 120 },
              { title: '操作', key: 'actions', width: 160, render: (_, r) => (
                <Space>
                  <Button onClick={() => openNewVersion('cts', r)}>新版本</Button>
                </Space>
              )}
            ]}
          />
        </div>
      )
    },
    {
      key: 'pts',
      label: 'PT',
      children: (
        <div>
          <LatestToggle checked={onlyLatest} onChange={setOnlyLatest} />
          <Space style={{ marginBottom: 12 }}>
            <Button type="primary" onClick={() => openAdd('pts')}>新增 PT</Button>
            <Button onClick={() => refreshTable('pts')}>刷新</Button>
          </Space>
          <Table
            rowKey="id"
            loading={loading.pts}
            dataSource={(onlyLatest ? groupLatestByName(pts) : pts)}
            columns={[
              ...colCommon,
              { title: '变比', dataIndex: 'ratio', key: 'ratio', width: 120 },
              { title: '操作', key: 'actions', width: 160, render: (_, r) => (
                <Space>
                  <Button onClick={() => openNewVersion('pts', r)}>新版本</Button>
                </Space>
              )}
            ]}
          />
        </div>
      )
    },
    {
      key: 'relays',
      label: '继电器',
      children: (
        <div>
          <LatestToggle checked={onlyLatest} onChange={setOnlyLatest} />
          <Space style={{ marginBottom: 12 }}>
            <Button type="primary" onClick={() => openAdd('relays')}>新增继电器</Button>
            <Button onClick={() => refreshTable('relays')}>刷新</Button>
          </Space>
          <Table
            rowKey="id"
            loading={loading.relays}
            dataSource={(onlyLatest ? groupLatestByName(relays) : relays)}
            columns={[
              ...colCommon,
              { title: '型号', dataIndex: 'model', key: 'model', width: 160 },
              { title: '操作', key: 'actions', width: 160, render: (_, r) => (
                <Space>
                  <Button onClick={() => openNewVersion('relays', r)}>新版本</Button>
                </Space>
              )}
            ]}
          />
        </div>
      )
    },
    {
      key: 'groundTypes',
      label: '接地类型模板',
      children: (
        <div>
          <Space style={{ marginBottom: 12 }}>
            <Button type="primary" onClick={() => openAdd('groundTypes')}>新增/编辑模板</Button>
            <Button onClick={() => refreshTable('groundTypes')}>刷新</Button>
          </Space>
          <Table rowKey="key" loading={loading.groundTypes} dataSource={groundTypes} columns={groundTypeColumns} />
        </div>
      )
    }
  ];

  const renderFormFields = () => {
    if (modalType === 'buses') {
      return (
        <>
          <Form.Item name="name" label="名称" rules={[{ required: true }]}><Input /></Form.Item>
          <Form.Item name="voltage_kV" label="电压(kV)" rules={[{ required: true }]}><InputNumber min={0} step={0.1} style={{ width: '100%' }} /></Form.Item>
          <Form.Item name="version" label="版本"><InputNumber min={1} /></Form.Item>
          <Form.Item name="status" label="状态"><Select options={[{ value: 'active', label: 'active' }, { value: 'archived', label: 'archived' }]} /></Form.Item>
        </>
      );
    }
    if (modalType === 'transformers') {
      return (
        <>
          <Form.Item name="name" label="名称" rules={[{ required: true }]}><Input /></Form.Item>
          <Form.Item name="S_tr_MVA" label="容量(MVA)" rules={[{ required: true }]}><InputNumber min={0} step={0.1} style={{ width: '100%' }} /></Form.Item>
          <Form.Item name="uk_percent" label="短路阻抗(%)" rules={[{ required: true }]}><InputNumber min={0} step={0.1} style={{ width: '100%' }} /></Form.Item>
          <Form.Item name="vector_group" label="接线组"><Input placeholder="如 Dyn11" /></Form.Item>
          <Form.Item name="secondary_neutral_ground" label="中性点接地"><Select options={[{ value: 'solid', label: '实心' }, { value: 'resistor', label: '电阻' }, { value: 'reactor', label: '电抗' }, { value: 'none', label: '不接地' }]} /></Form.Item>
          <Form.Item name="version" label="版本"><InputNumber min={1} /></Form.Item>
          <Form.Item name="status" label="状态"><Select options={[{ value: 'active', label: 'active' }, { value: 'archived', label: 'archived' }]} /></Form.Item>
        </>
      );
    }
    if (modalType === 'lines') {
      return (
        <>
          <Form.Item name="name" label="名称" rules={[{ required: true }]}><Input /></Form.Item>
          <Form.Item name="length_km" label="长度(km)" rules={[{ required: true }]}><InputNumber min={0} step={0.01} style={{ width: '100%' }} /></Form.Item>
          <Form.Item name="R_per_km_ohm" label="R(Ω/km)" rules={[{ required: true }]}><InputNumber min={0} step={0.0001} style={{ width: '100%' }} /></Form.Item>
          <Form.Item name="X_per_km_ohm" label="X(Ω/km)" rules={[{ required: true }]}><InputNumber min={0} step={0.0001} style={{ width: '100%' }} /></Form.Item>
          <Form.Item name="z0_factor" label="零序系数"><InputNumber min={0} step={0.01} style={{ width: '100%' }} /></Form.Item>
          <Form.Item name="version" label="版本"><InputNumber min={1} /></Form.Item>
          <Form.Item name="status" label="状态"><Select options={[{ value: 'active', label: 'active' }, { value: 'archived', label: 'archived' }]} /></Form.Item>
        </>
      );
    }
    if (modalType === 'feeders') {
      return (
        <>
          <Form.Item name="label" label="标识" rules={[{ required: true }]}><Input placeholder="如 FDR-01" /></Form.Item>
          <Form.Item name="name" label="名称"><Input /></Form.Item>
          <Form.Item name="bus_id" label="母线" rules={[{ required: true }]}><Select options={busOptions} showSearch /></Form.Item>
          <Form.Item name="transformer_id" label="变压器" rules={[{ required: true }]}><Select options={transformerOptions} showSearch /></Form.Item>
          <Form.Item name="line_id" label="线路"><Select options={lineOptions} showSearch allowClear /></Form.Item>
          <Form.Item name="ct_id" label="CT引用"><Select options={ctOptions} showSearch allowClear /></Form.Item>
          <Form.Item name="pt_id" label="PT引用"><Select options={ptOptions} showSearch allowClear /></Form.Item>
          <Form.Item name="relay_id" label="继电器"><Select options={relayOptions} showSearch allowClear /></Form.Item>
          <Form.Item name="rated_current" label="额定电流(A)"><InputNumber min={0} step={1} style={{ width: '100%' }} /></Form.Item>
          <Form.Item name="ct_ratio" label="CT变比"><InputNumber min={0} step={1} style={{ width: '100%' }} /></Form.Item>
          <Form.Item name="source_S_sc_MVA" label="一次侧短路容量(MVA)"><InputNumber min={0} step={1} style={{ width: '100%' }} /></Form.Item>
          <Form.Item name="source_X_R" label="一次侧X/R"><InputNumber min={0} step={0.1} style={{ width: '100%' }} /></Form.Item>
          <Form.Item name="ground_R_ohm" label="接地R(Ω)"><InputNumber min={0} step={0.01} style={{ width: '100%' }} /></Form.Item>
          <Form.Item name="ground_X_ohm" label="接地X(Ω)"><InputNumber min={0} step={0.01} style={{ width: '100%' }} /></Form.Item>
          <Form.Item name="version" label="版本"><InputNumber min={1} /></Form.Item>
          <Form.Item name="status" label="状态"><Select options={[{ value: 'active', label: 'active' }, { value: 'archived', label: 'archived' }]} /></Form.Item>
        </>
      );
    }
    if (modalType === 'groundTypes') {
       return (
         <>
           <Form.Item name="key" label="Key" rules={[{ required: true }]}><Input /></Form.Item>
           <Form.Item name="label" label="名称" rules={[{ required: true }]}><Input /></Form.Item>
           <Form.Item name="default_R_ohm" label="默认R(Ω)"><InputNumber min={0} step={0.01} style={{ width: '100%' }} /></Form.Item>
           <Form.Item name="default_X_ohm" label="默认X(Ω)"><InputNumber min={0} step={0.01} style={{ width: '100%' }} /></Form.Item>
         </>
       );
     }
     if (modalType === 'cts') {
       return (
         <>
           <Form.Item name="name" label="名称" rules={[{ required: true }]}><Input /></Form.Item>
           <Form.Item name="ratio" label="变比" rules={[{ required: true }]}><InputNumber min={0} step={1} style={{ width: '100%' }} /></Form.Item>
           <Form.Item name="version" label="版本"><InputNumber min={1} /></Form.Item>
           <Form.Item name="status" label="状态"><Select options={[{ value: 'active', label: 'active' }, { value: 'archived', label: 'archived' }]} /></Form.Item>
         </>
       );
     }
     if (modalType === 'pts') {
       return (
         <>
           <Form.Item name="name" label="名称" rules={[{ required: true }]}><Input /></Form.Item>
           <Form.Item name="ratio" label="变比" rules={[{ required: true }]}><InputNumber min={0} step={1} style={{ width: '100%' }} /></Form.Item>
           <Form.Item name="version" label="版本"><InputNumber min={1} /></Form.Item>
           <Form.Item name="status" label="状态"><Select options={[{ value: 'active', label: 'active' }, { value: 'archived', label: 'archived' }]} /></Form.Item>
         </>
       );
     }
     if (modalType === 'relays') {
       return (
         <>
           <Form.Item name="name" label="名称"><Input /></Form.Item>
           <Form.Item name="model" label="型号" rules={[{ required: true }]}><Input /></Form.Item>
           <Form.Item name="version" label="版本"><InputNumber min={1} /></Form.Item>
           <Form.Item name="status" label="状态"><Select options={[{ value: 'active', label: 'active' }, { value: 'archived', label: 'archived' }]} /></Form.Item>
         </>
       );
     }
     return null;
   };

  return (
    <div style={{ padding: 16 }}>
      <h2>设备库管理</h2>
      <Tabs activeKey={activeKey} onChange={setActiveKey} items={tabItems} />

      <Modal open={modalOpen} onCancel={() => setModalOpen(false)} onOk={handleSubmit} title={editingRecord ? (modalType === 'groundTypes' ? '编辑模板' : '创建新版本') : '新增'} okText={editingRecord ? (modalType === 'groundTypes' ? '保存' : '保存新版本') : '保存'}>
        <Form form={form} layout="vertical">
          {renderFormFields()}
        </Form>
      </Modal>

      <Modal
        open={importReportOpen}
        onCancel={() => setImportReportOpen(false)}
        onOk={() => setImportReportOpen(false)}
        title="导入报告"
        width={800}
      >
        {!importReport ? (
           <div>暂无报告</div>
         ) : (
          <div ref={importReportRef}>
            {importReport?.meta?.dryRun && (
              <div style={{ marginBottom: 8 }}>
                <Tag color="orange">仅校验 (dry run)</Tag>
              </div>
            )}
            <div style={{ marginBottom: 8 }}>
              汇总：有效 {importTotals.totalValid}，无效 {importTotals.totalInvalid}；插入 {importTotals.totalInserted}，更新 {importTotals.totalUpserted}，失败 {importTotals.totalFailed}
            </div>
            <Space style={{ marginBottom: 8 }}>
              <span>显示详情</span>
              <Switch size="small" checked={showImportDetails} onChange={setShowImportDetails} />
              <span>精简摘要</span>
              <Switch size="small" checked={simpleImportSummary} onChange={setSimpleImportSummary} />
              <Button onClick={() => {
                const pr = resolveFilenamePrefix(devicePrefixTemplate, { name: 'Device Library Import Summary', project: '', line: '' });
                const base = buildFinalBaseName('Device Library Import Summary', pr);
                downloadTextFile(simpleImportSummary ? buildImportSummaryMarkdownSimple(importReport) : buildImportSummaryMarkdown(importReport), `${base}.md`, 'text/markdown;charset=utf-8');
              }}>下载摘要 Markdown</Button>
              <Button onClick={() => copyToClipboard(simpleImportSummary ? buildImportSummaryMarkdownSimple(importReport) : buildImportSummaryMarkdown(importReport))}>复制摘要</Button>
              <Button onClick={() => {
                const pr = resolveFilenamePrefix(devicePrefixTemplate, { name: 'Device Library Import Summary', project: '', line: '' });
                const base = buildFinalBaseName('Device Library Import Summary', pr);
                downloadTextFile(buildImportSummaryCsv(importReport), `${base}.csv`, 'text/csv;charset=utf-8');
              }}>下载摘要 CSV</Button>
            <Button onClick={() => {
              const pr = resolveFilenamePrefix(devicePrefixTemplate, { name: 'Device Library Import', project: '', line: '' });
              exportElementToHtml({ element: importReportRef.current, name: buildFinalBaseName('Device Library Import', pr), title: '设备库导入报告', appendTimestamp: false });
            }}>下载 HTML</Button>
            <Button onClick={() => {
              const pr = resolveFilenamePrefix(devicePrefixTemplate, { name: 'Device Library Import', project: '', line: '' });
              exportElementToWord({ element: importReportRef.current, name: buildFinalBaseName('Device Library Import', pr), title: '设备库导入报告', appendTimestamp: false });
            }}>下载 Word</Button>
            </Space>
            {showImportDetails && (
              <>
                <h4>数据校验</h4>
                <Table
                  size="small"
                   rowKey={(r, i) => `${r.table}-${i}`}
                   pagination={false}
                   dataSource={(importReport.validation?.summary || []).map((s, i) => ({ key: i, ...s }))}
                   columns={[
                     { title: '表', dataIndex: 'table', key: 'table', width: 180 },
                     { title: '有效', dataIndex: 'valid', key: 'valid', width: 100 },
                     { title: '无效', dataIndex: 'invalid', key: 'invalid', width: 100 },
                   ]}
                 />
                <h4 style={{ marginTop: 16 }}>数据库写入</h4>
                <Table
                   size="small"
                   rowKey={(r, i) => `${r.table}-${i}`}
                   pagination={false}
                   dataSource={Object.keys(importReport.dbResults || {}).map((t, i) => {
                     const r = importReport.dbResults[t] || {}; return { key: `${t}-${i}`, table: t, inserted: r.insertedCount || 0, upserted: r.upsertedCount || 0, failed: r.failedCount || 0, dryRun: r.dryRun ? 'Yes' : 'No' };
                   })}
                   columns={[
                     { title: '表', dataIndex: 'table', key: 'table', width: 180 },
                     { title: '插入', dataIndex: 'inserted', key: 'inserted', width: 100 },
                     { title: '更新', dataIndex: 'upserted', key: 'upserted', width: 100 },
                     { title: '失败', dataIndex: 'failed', key: 'failed', width: 100 },
                     { title: '仅校验', dataIndex: 'dryRun', key: 'dryRun', width: 100 },
                   ]}
                 />
              </>
            )}

            <Space style={{ marginTop: 16 }}>
              <Button onClick={() => downloadJson(importReport, 'device-library-import-report.json')}>下载详细报告</Button>
              <Button onClick={() => copyToClipboard(JSON.stringify(importReport, null, 2))}>复制统计</Button>
              <Button type="primary" onClick={() => setImportReportOpen(false)}>关闭</Button>
            </Space>
          </div>
         )}
       </Modal>
 
       <Modal
         open={importOptionsOpen}
        onCancel={() => setImportOptionsOpen(false)}
        onOk={handleConfirmImport}
        title="导入选项"
        width={520}
      >
        <Space direction="vertical" style={{ width: '100%' }}>
          <div>
            <span style={{ marginRight: 8 }}>文件</span>
            <Space>
              <Input style={{ width: 300 }} readOnly value={importOptions.fileName || '未选择'} />
              <Button onClick={handlePickImportFile}>选择文件</Button>
            </Space>
          </div>
          <div>
            <span style={{ marginRight: 8 }}>导入模式</span>
            <Select
              value={importOptions.mode}
              onChange={(v) => setImportOptions(prev => ({ ...prev, mode: v }))}
              options={[
                { value: 'upsert', label: '覆盖/更新 (upsert)' },
                { value: 'insert', label: '仅插入 (insert)' }
              ]}
              style={{ width: 220 }}
            />
          </div>
          <div>
            <span style={{ marginRight: 8 }}>仅校验 (dry run)</span>
            <Switch checked={importOptions.dryRun} onChange={(v) => setImportOptions(prev => ({ ...prev, dryRun: v }))} />
            <span style={{ marginLeft: 8, color: '#888' }}>不写入数据库，仅显示校验与预期统计</span>
          </div>
       </Space>
      </Modal>

      {/* 厂商曲线导入弹窗 */}
      <Modal
        open={vendorImportOpen}
        onCancel={() => setVendorImportOpen(false)}
        title="导入厂商曲线"
        width={720}
        footer={(
          <Space>
            <Button data-testid="vendor-import-parse" onClick={handleParseVendorCurves}>解析</Button>
            <Button data-testid="vendor-import-add-to-overlay" type="primary" disabled={!vendorParsedRecords.length} onClick={handleAddVendorCurvesToOverlay}>添加到叠加</Button>
            <Button data-testid="vendor-import-close" onClick={() => setVendorImportOpen(false)}>关闭</Button>
          </Space>
        )}
     >
        <Space direction="vertical" style={{ width: '100%' }}>
          <Alert type="info" showIcon message="支持 JSON 与 CSV。JSON 可为数组或对象，字段：label、curveType、timeMultiplier(TMS)、pickupCurrent、axesType(默认 I-t)。CSV 需包含列：label,curveType,timeMultiplier,pickupCurrent[,color][,axesType]。" />
          <div>
            <span style={{ marginRight: 8 }}>文件</span>
            <Space>
              <Input style={{ width: 360 }} readOnly value={vendorImportFileName || '未选择'} />
              <Button data-testid="vendor-import-pick-file" onClick={handlePickVendorFile}>选择文件</Button>
            </Space>
          </div>
          <div>
            <span style={{ marginRight: 8 }}>统一颜色</span>
            <Space>
              <Switch
                checked={vendorUseUnifiedColor}
                onChange={setVendorUseUnifiedColor}
                data-testid="vendor-import-unified-color-switch"
              />
              <ColorPicker
                disabled={!vendorUseUnifiedColor}
                value={vendorUnifiedColor}
                onChange={(c) => setVendorUnifiedColor(c.toHexString())}
                size="small"
                data-testid="vendor-import-unified-color-picker"
              />
            </Space>
            <span style={{ marginLeft: 8, color: '#888' }}>开启后为所有曲线套用同一颜色</span>
          </div>
          {!!vendorParsedRecords.length && (
            <>
              <div style={{ color: '#888' }}>已解析曲线：{vendorParsedRecords.length}</div>
              <Table
                size="small"
                pagination={false}
                rowKey={(r, i) => r.id || i}
                dataSource={vendorParsedRecords.map(r => ({
                  label: r?.result?.name || r?.id || '曲线',
                  curveType: r?.results?.curveType,
                  timeMultiplier: r?.results?.timeMultiplier,
                  pickupCurrent: r?.results?.pickupCurrent,
                  axesType: r?.results?.axesType || 'I-t',
                  color: r?.meta?.color
                }))}
                columns={[
                  { title: '标签', dataIndex: 'label', key: 'label', width: 180 },
                  { title: '曲线', dataIndex: 'curveType', key: 'curveType', width: 120 },
                  { title: 'TMS', dataIndex: 'timeMultiplier', key: 'timeMultiplier', width: 120 },
                  { title: 'Pickup(A)', dataIndex: 'pickupCurrent', key: 'pickupCurrent', width: 140 },
                  { title: '轴类型', dataIndex: 'axesType', key: 'axesType', width: 120 },
                  { title: '颜色', dataIndex: 'color', key: 'color', width: 100, render: (v) => (v ? <span title={v} style={{ display: 'inline-block', width: 16, height: 16, borderRadius: 2, border: '1px solid #ddd', background: v }} /> : <span style={{ color: '#bbb' }}>默认</span>) },
                ]}
              />
            </>
          )}
          {!!vendorParseErrors.length && (
            <Alert type="warning" showIcon message={`解析警告：${vendorParseErrors.length} 条`} description={(
              <div style={{ maxHeight: 160, overflowY: 'auto' }}>
                {vendorParseErrors.map((e, idx) => (
                  <div key={idx} style={{ fontSize: 12, color: '#a00' }}>- {e}</div>
                ))}
              </div>
            )} />
          )}
        </Space>
      </Modal>

      <Modal
        open={exportOptionsOpen}
        onCancel={() => setExportOptionsOpen(false)}
        onOk={handleConfirmExport}
        title="导出选项"
        width={520}
      >
        <Space direction="vertical" style={{ width: '100%' }}>
          <div>
            <span style={{ marginRight: 8 }}>文件前缀</span>
            <Input style={{ width: 240 }} value={exportOptions.fileNamePrefix} onChange={(e) => setExportOptions(prev => ({ ...prev, fileNamePrefix: e.target.value }))} />
          </div>
          <div>
            <span style={{ marginRight: 8 }}>仅导出最新版本</span>
            <Switch checked={exportOptions.onlyLatest} onChange={(v) => setExportOptions(prev => ({ ...prev, onlyLatest: v }))} />
          </div>
          <div>
            <span style={{ marginRight: 8 }}>导出表</span>
            <Checkbox.Group
              options={[
                { label: '接地类型', value: 'ground_types' },
                { label: '母线', value: 'buses' },
                { label: '变压器', value: 'transformers' },
                { label: '线路', value: 'lines' },
                { label: 'CT', value: 'cts' },
                { label: 'PT', value: 'pts' },
                { label: '继电器', value: 'relays' },
                { label: '馈线', value: 'feeders' },
              ]}
              value={exportOptions.tables}
              onChange={(vals) => setExportOptions(prev => ({ ...prev, tables: vals }))}
            />
            <Space style={{ marginTop: 8 }}>
              <Button size="small" onClick={() => setExportOptions(prev => ({ ...prev, tables: ['ground_types','buses','transformers','lines','cts','pts','relays','feeders'] }))}>全选</Button>
              <Button size="small" onClick={() => setExportOptions(prev => ({ ...prev, tables: [] }))}>全不选</Button>
              <Button size="small" onClick={() => setExportOptions(prev => ({ ...prev, tables: ['ground_types','buses','transformers','lines','cts','pts','relays','feeders'].filter(t => !(prev.tables || []).includes(t)) }))}>反选</Button>
            </Space>
           </div>
         </Space>
       </Modal>
 
      <Modal
        open={exportReportOpen}
         onCancel={() => setExportReportOpen(false)}
         onOk={() => setExportReportOpen(false)}
         title="导出报告"
         width={640}
       >
        {!exportReport ? (
           <div>暂无报告</div>
         ) : (
          <div ref={exportReportRef}>
             <div style={{ marginBottom: 8 }}>文件：{exportReport.filename}</div>
             <div style={{ marginBottom: 8 }}>仅最新版本：{exportReport.onlyLatest ? '是' : '否'}</div>
             <div style={{ marginBottom: 8 }}>导出时间：{exportReport.exportedAt}</div>
             <div style={{ marginBottom: 8 }}>导出表数量：{exportReport.tableCount ?? (exportReport.selectedTables?.length || 0)}</div>
             <div style={{ marginBottom: 8 }}>总条目数：{exportTotalCount}</div>
             <div style={{ marginBottom: 8 }}>
               选定表：
               <Space wrap>
                 {(exportReport.selectedTables || []).map(t => <Tag key={t}>{TABLE_LABELS[t] || t}</Tag>)}
               </Space>
             </div>
            <Table
               size="small"
               rowKey={(r) => r.table}
               pagination={false}
               dataSource={Object.keys(exportReport.counts || {}).map((t) => ({ table: t, count: exportReport.counts[t] || 0 }))}
               columns={[
                 { title: '表', dataIndex: 'table', key: 'table', width: 200 },
                 { title: '条目数', dataIndex: 'count', key: 'count', width: 120 },
               ]}
             />
            {showExportDetails && (
              <Table
                size="small"
                rowKey={(r) => r.table}
                pagination={false}
                dataSource={Object.keys(exportReport.counts || {}).map((t) => ({ table: t, count: exportReport.counts[t] || 0 }))}
                columns={[
                  { title: '表', dataIndex: 'table', key: 'table', width: 200 },
                  { title: '条目数', dataIndex: 'count', key: 'count', width: 120 },
                ]}
              />
            )}
             <Space style={{ marginTop: 16 }}>
               <span>摘要模式：</span>
               <Switch size="small" checked={simpleExportSummary} onChange={setSimpleExportSummary} />
               <Button onClick={() => downloadJson(exportReport, 'device-library-export-report.json')}>下载导出报告</Button>
               <Button onClick={() => copyToClipboard(JSON.stringify(exportReport, null, 2))}>复制统计</Button>
               <Button onClick={() => {
                 const pr = resolveFilenamePrefix(devicePrefixTemplate, { name: 'Device Library Export Summary', project: '', line: '' });
                 const base = buildFinalBaseName('Device Library Export Summary', pr);
                 downloadTextFile(simpleExportSummary ? buildExportSummaryMarkdownSimple(exportReport) : buildExportSummaryMarkdown(exportReport), `${base}.md`, 'text/markdown;charset=utf-8');
               }}>下载摘要 Markdown</Button>
               <Button onClick={() => copyToClipboard(simpleExportSummary ? buildExportSummaryMarkdownSimple(exportReport) : buildExportSummaryMarkdown(exportReport))}>复制摘要</Button>
               <Button onClick={() => {
                 const pr = resolveFilenamePrefix(devicePrefixTemplate, { name: 'Device Library Export Summary', project: '', line: '' });
                 const base = buildFinalBaseName('Device Library Export Summary', pr);
                 downloadTextFile(buildExportSummaryCsv(exportReport), `${base}.csv`, 'text/csv;charset=utf-8');
               }}>下载摘要 CSV</Button>
            <Button onClick={() => {
              const pr = resolveFilenamePrefix(devicePrefixTemplate, { name: 'Device Library Export', project: '', line: '' });
              exportElementToHtml({ element: exportReportRef.current, name: buildFinalBaseName('Device Library Export', pr), title: '设备库导出报告', appendTimestamp: false });
            }}>下载 HTML</Button>
            <Button onClick={() => {
              const pr = resolveFilenamePrefix(devicePrefixTemplate, { name: 'Device Library Export', project: '', line: '' });
              exportElementToWord({ element: exportReportRef.current, name: buildFinalBaseName('Device Library Export', pr), title: '设备库导出报告', appendTimestamp: false });
            }}>下载 Word</Button>
               <Button type="primary" onClick={() => setExportReportOpen(false)}>关闭</Button>
             </Space>
           </div>
         )}
       </Modal>

      {/* 叠加校核配置弹窗 */}
      <Modal
        open={overlayOptionsOpen}
        onCancel={() => setOverlayOptionsOpen(false)}
        onOk={handleComputeOverlayAndOpen}
        title="叠加校核选项"
        width={520}
      >
        <Form layout="vertical">
          <Form.Item label="保护类型">
            <Select
              value={overlayOptions.protectionType}
              onChange={(v) => setOverlayOptions(prev => ({ ...prev, protectionType: v }))}
              options={[
                { value: 'overcurrent', label: '过电流 (I-t)' },
                { value: 'directional', label: '方向 (I-t)' },
                { value: 'zero_sequence', label: '零序 (I-t)' },
                { value: 'distance', label: '距离 (Z)' },
                { value: 'differential', label: '差动 (Id-Ir)' },
                { value: 'longitudinal', label: '纵联差动 (I-t)' },
              ]}
            />
          </Form.Item>

          {(['overcurrent','directional','zero_sequence','longitudinal'].includes(overlayOptions.protectionType)) && (
            <Space style={{ marginBottom: 8 }}>
              <span>曲线类型</span>
              <Select
                style={{ width: 180 }}
                value={overlayOptions.curveType}
                onChange={(v) => setOverlayOptions(prev => ({ ...prev, curveType: v }))}
                options={[
                  { value: 'Normal Inverse', label: 'NI' },
                  { value: 'Very Inverse', label: 'VI' },
                  { value: 'Extremely Inverse', label: 'EI' },
                  { value: 'Long-time Inverse', label: 'LTI' },
                ]}
              />
              <span>时间倍数 TMS</span>
              <InputNumber min={0.05} max={1.0} step={0.05} value={overlayOptions.timeMultiplier} onChange={(v) => setOverlayOptions(prev => ({ ...prev, timeMultiplier: typeof v === 'number' ? v : prev.timeMultiplier }))} />
            </Space>
          )}

          {overlayOptions.protectionType === 'directional' && (
            <Form.Item label="极化角(°)">
              <InputNumber min={0} max={90} step={1} value={overlayOptions.polarizingAngle} onChange={(v) => setOverlayOptions(prev => ({ ...prev, polarizingAngle: typeof v === 'number' ? v : prev.polarizingAngle }))} />
            </Form.Item>
          )}

          {overlayOptions.protectionType === 'zero_sequence' && (
            <Form.Item label="零序比例系数 k0">
              <InputNumber min={0} max={1} step={0.05} value={overlayOptions.k0} onChange={(v) => setOverlayOptions(prev => ({ ...prev, k0: typeof v === 'number' ? v : prev.k0 }))} />
            </Form.Item>
          )}

          {(overlayOptions.protectionType === 'distance' || overlayOptions.protectionType === 'directional') && (
            <Form.Item label="PT变比覆盖(可选)">
              <InputNumber min={1} step={1} placeholder="留空使用设备库 PT" value={overlayOptions.overridePtRatio} onChange={(v) => setOverlayOptions(prev => ({ ...prev, overridePtRatio: typeof v === 'number' ? v : undefined }))} />
            </Form.Item>
          )}

          <Space style={{ marginTop: 8 }}>
            <span>故障类型</span>
            <Select
              value={overlayOptions.faultType}
              onChange={(v) => setOverlayOptions(prev => ({ ...prev, faultType: v }))}
              options={[
                { value: '3P', label: '三相' },
                { value: 'LL', label: '两相' },
                { value: '1P-G', label: '单相接地' },
              ]}
              style={{ width: 160 }}
            />
            <span>工况</span>
            <Select
              value={overlayOptions.scenario}
              onChange={(v) => setOverlayOptions(prev => ({ ...prev, scenario: v }))}
              options={[
                { value: 'max', label: '最大' },
                { value: 'min', label: '最小' },
              ]}
              style={{ width: 120 }}
            />
          </Space>
        </Form>
      </Modal>

      {/* 叠加校核展示弹窗 */}
      <TccOverlayModal ref={overlayRef} visible={overlayOpen} onClose={() => setOverlayOpen(false)} records={overlayRecords} filenamePrefix={overlaySettings?.filenamePrefix || ''} defaultMinMargin={overlaySettings?.minMargin ?? 0.3} />

      {/* 叠加设置弹窗 */}
      <Modal
        open={overlaySettingsOpen}
        onCancel={() => setOverlaySettingsOpen(false)}
        onOk={() => setOverlaySettingsOpen(false)}
        title="叠加设置"
        width={520}
      >
        <Form layout="vertical">
          <Form.Item label="排序方式">
            <Select
              value={overlaySettings.sortMode}
              onChange={(v) => setOverlaySettings(prev => ({ ...prev, sortMode: v }))}
              options={[
                { value: 'byRatedCurrent', label: '按额定电流升序（默认，下级→上级）' },
                { value: 'bySelectionOrder', label: '按选中顺序（第一个为下级）' },
              ]}
            />
          </Form.Item>
          <Form.Item>
            <Alert
              type="info"
              showIcon
              message="提示"
              description={(
                <div>
                  <div>排序方式决定叠加顺序及下级→上级关系。第 1 条为下级(基准)。</div>
                  <div>最小选择性裕度用于 I–t 曲线的时间差判定，推荐 0.2–0.5s。</div>
                  <div>选择“按选中顺序”时，可用上移/下移或“重置选中顺序”快速调整。</div>
                </div>
              )}
            />
          </Form.Item>
          <Form.Item label="最小选择性裕度(s)">
            <InputNumber min={0} max={2} step={0.05} value={overlaySettings.minMargin}
              onChange={(v) => setOverlaySettings(prev => ({ ...prev, minMargin: (typeof v === 'number' ? v : 0.3) }))} />
          </Form.Item>
          <Form.Item label="导出文件名前缀">
            <Input placeholder="例如：项目A_线路1" value={overlaySettings.filenamePrefix}
                   onChange={(e) => setOverlaySettings(prev => ({ ...prev, filenamePrefix: e.target.value }))} />
          </Form.Item>
          {overlaySettings.sortMode === 'bySelectionOrder' ? (
            <div style={{ marginTop: 8 }}>
              <div style={{ fontWeight: 500 }}>选中顺序（第一个为下级）</div>
              <Space style={{ marginTop: 6 }}>
                <Button size="small" onClick={() => setSelectedFeederOrder(selectedFeederRowKeys || [])}>重置选中顺序</Button>
              </Space>
              <Space direction="vertical" style={{ width: '100%', marginTop: 8 }}>
                {(selectedFeederOrder || []).map((id, idx) => {
                  const row = (selectedFeederRows || []).find(r => r.id === id);
                  const label = row?.label || row?.name || id;
                  return (
                    <div key={id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <span>{idx + 1}. {label}</span>
                      <Space>
                        <Button size="small" disabled={idx === 0} onClick={() => setSelectedFeederOrder(prev => {
                          const next = [...prev];
                          [next[idx - 1], next[idx]] = [next[idx], next[idx - 1]];
                          return next;
                        })}>上移</Button>
                        <Button size="small" disabled={idx === (selectedFeederOrder.length - 1)} onClick={() => setSelectedFeederOrder(prev => {
                          const next = [...prev];
                          [next[idx + 1], next[idx]] = [next[idx], next[idx + 1]];
                          return next;
                        })}>下移</Button>
                      </Space>
                    </div>
                  );
                })}
              </Space>
            </div>
          ) : null}
        </Form>
      </Modal>
    </div>
  );
};

export default DeviceLibraryPage;

const TABLE_LABELS = { ground_types: '接地类型', buses: '母线', transformers: '变压器', lines: '线路', cts: 'CT', pts: 'PT', relays: '继电器', feeders: '馈线' };